import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { QrCode, Upload, CheckCircle, Loader2, ArrowLeft, Copy, Check, XCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Payment() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [pixConfig, setPixConfig] = useState({ pix_key: null, pix_qr_code: null, pix_value: null });
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [receiptBase64, setReceiptBase64] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [copied, setCopied] = useState(false);
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  useEffect(() => {
    fetchPixConfig();
    if (token) {
      checkPaymentStatus();
    }
  }, [token]);

  const fetchPixConfig = async () => {
    try {
      const response = await axios.get(`${API}/payment/config`);
      setPixConfig(response.data);
    } catch (error) {
      console.error('Error fetching PIX config:', error);
    }
  };

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/payment/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentStatus(response.data);
      if (response.data.has_payment && response.data.status === 'pending') {
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showNotification('A imagem deve ter no máximo 5MB', 'error');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setReceiptPreview(reader.result);
        setReceiptBase64(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!receiptBase64) {
      showNotification('Por favor, envie o comprovante de pagamento', 'error');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/payment/submit`,
        { receipt_image: receiptBase64 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmitted(true);
      showNotification('Comprovante enviado! Aguarde a aprovação.');
    } catch (error) {
      showNotification(error.response?.data?.detail || 'Erro ao enviar comprovante', 'error');
    } finally {
      setLoading(false);
    }
  };

  const copyPixKey = () => {
    if (pixConfig.pix_key) {
      navigator.clipboard.writeText(pixConfig.pix_key);
      setCopied(true);
      showNotification('Chave PIX copiada!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (submitted || (paymentStatus?.has_payment && paymentStatus?.status === 'pending')) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <Card className="max-w-md w-full border-0 shadow-xl">
          <CardContent className="pt-12 pb-8 px-8 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            
            <h1 className="text-2xl font-bold mb-3" data-testid="payment-submitted-title">
              Pagamento Enviado com Sucesso!
            </h1>
            
            <p className="text-muted-foreground mb-4 leading-relaxed">
              Seu comprovante foi recebido e está sendo analisado pela nossa equipe.
            </p>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-amber-800 text-sm font-medium">
                ⏳ Aguarde a liberação do seu acesso
              </p>
              <p className="text-amber-600 text-xs mt-1">
                Você receberá acesso completo assim que o pagamento for confirmado. 
                Isso geralmente leva até 24 horas úteis.
              </p>
            </div>
            
            <Link to="/login" data-testid="back-to-login">
              <Button className="w-full h-11 rounded-full">
                Voltar ao Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
          notification.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
        }`}>
          {notification.type === 'error' ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {notification.message}
        </div>
      )}
      <div className="max-w-2xl mx-auto">
        <Link 
          to="/aguardando" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </Link>
        
        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-bold">Pagamento via PIX</CardTitle>
            <CardDescription>
              Escaneie o QR Code ou copie a chave PIX para realizar o pagamento
            </CardDescription>
            {pixConfig.pix_value && (
              <div className="mt-4 p-4 bg-orange-50 rounded-xl">
                <p className="text-sm text-slate-600">Valor da assinatura:</p>
                <p className="text-3xl font-bold text-orange-600">R$ {pixConfig.pix_value}</p>
              </div>
            )}
          </CardHeader>
          
          <CardContent className="space-y-8 pt-6">
            {/* QR Code Section */}
            <div className="flex flex-col items-center">
              {pixConfig.pix_qr_code ? (
                <div className="bg-white p-4 rounded-2xl shadow-inner border">
                  <img 
                    src={pixConfig.pix_qr_code} 
                    alt="QR Code PIX" 
                    className="w-64 h-64 object-contain"
                    data-testid="pix-qr-code"
                  />
                </div>
              ) : (
                <div className="w-72 h-72 bg-muted rounded-2xl flex flex-col items-center justify-center">
                  <QrCode className="w-16 h-16 text-muted-foreground mb-4" />
                  <p className="text-sm text-muted-foreground text-center px-4">
                    QR Code não configurado pelo administrador
                  </p>
                </div>
              )}
            </div>
            
            {/* PIX Key */}
            {pixConfig.pix_key && (
              <div className="space-y-2">
                <Label>Chave PIX</Label>
                <div className="flex gap-2">
                  <div className="flex-1 bg-muted rounded-lg px-4 py-3 font-mono text-sm break-all">
                    {pixConfig.pix_key}
                  </div>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={copyPixKey}
                    data-testid="copy-pix-key-btn"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}
            
            {/* Upload Receipt */}
            <div className="space-y-4">
              <div className="border-t pt-6">
                <Label className="text-base font-semibold">Enviar Comprovante</Label>
                <p className="text-sm text-muted-foreground mt-1 mb-4">
                  Após realizar o pagamento, envie o comprovante para liberação do acesso.
                </p>
              </div>
              
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
                data-testid="receipt-file-input"
              />
              
              {receiptPreview ? (
                <div className="space-y-4">
                  <div className="relative rounded-xl overflow-hidden border">
                    <img 
                      src={receiptPreview} 
                      alt="Comprovante" 
                      className="w-full max-h-80 object-contain bg-muted"
                    />
                  </div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="change-receipt-btn"
                  >
                    Trocar imagem
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full h-32 border-dashed"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-receipt-btn"
                >
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-8 h-8 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      Clique para enviar o comprovante
                    </span>
                  </div>
                </Button>
              )}
            </div>
            
            <Button
              className="w-full h-12 rounded-full font-medium"
              disabled={!receiptBase64 || loading}
              onClick={handleSubmit}
              data-testid="submit-payment-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Enviar Comprovante'
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
